/**
 * AdminMeetingTypeTag Components
 * Tags for displaying meeting types with full detail (1:1, group session, recurring)
 * For use in admin and people team contexts where the distinction between all types matters.
 *
 * Variants:
 * - IconBadge: Small square with just the icon (20x21)
 * - Badge: Pill with icon and text label
 */

import {
  Box, HStack, Icon, Text, Tooltip,
} from "@chakra-ui/react";
import CaminoOneOnOneIcon from "design-system/icons/OneOnOne";
import CaminoGroupSessionIcon from "design-system/icons/GroupSession";
import CaminoRecurringIcon from "design-system/icons/Recurring";
import { TagSize, ICON_BADGE_SIZE_STYLES, PILL_BADGE_SIZE_STYLES } from "design-system/foundation/TagSizeStyles";

const MEETING_CONFIG = {
  one_on_one: {
    label: "1:1 Meeting",
    icon: CaminoOneOnOneIcon,
    fillColor: "meetingOneOnOne.icon",
    bgColor: "meetingOneOnOne.bg",
  },
  group_session: {
    label: "Group Session Meeting",
    icon: CaminoGroupSessionIcon,
    fillColor: "meetingGroupSession.icon",
    bgColor: "meetingGroupSession.bg",
  },
  recurring: {
    label: "Recurring Meeting",
    icon: CaminoRecurringIcon,
    fillColor: "meetingRecurring.icon",
    bgColor: "meetingRecurring.bg",
  },
} as const;

type MeetingTypeKey = keyof typeof MEETING_CONFIG;

// Domain model uses these values
type DomainMeetingType = "individual" | "group_session_adhoc" | "group_session_recurring";

// Accept either domain values or design system values
export type MeetingTypeInput = MeetingTypeKey | DomainMeetingType;

const DOMAIN_MEETING_TYPE_MAP: Record<DomainMeetingType, MeetingTypeKey> = {
  individual: "one_on_one",
  group_session_adhoc: "group_session",
  group_session_recurring: "recurring",
};

function resolveMeetingType(meetingType: MeetingTypeInput): MeetingTypeKey {
  if (meetingType in DOMAIN_MEETING_TYPE_MAP) {
    return DOMAIN_MEETING_TYPE_MAP[meetingType as DomainMeetingType];
  }
  return meetingType as MeetingTypeKey;
}

interface MeetingTypeIconBadgeProps {
  meetingType: MeetingTypeInput;
  size?: TagSize;
}

function MeetingTypeIconBadge({ meetingType, size = "md" }: MeetingTypeIconBadgeProps) {
  const config = MEETING_CONFIG[resolveMeetingType(meetingType)];
  const styles = ICON_BADGE_SIZE_STYLES[size];
  return (
    <Tooltip label={config.label} fontSize="xs">
      <Box
        as="span"
        display="inline-flex"
        alignItems="center"
        justifyContent="center"
        w={styles.w}
        h={styles.h}
        p={styles.p}
        bg={config.bgColor}
        borderRadius={styles.borderRadius}
      >
        <Icon as={config.icon} boxSize={styles.iconSize} color={config.fillColor} />
      </Box>
    </Tooltip>
  );
}

interface MeetingTypeBadgeProps {
  meetingType: MeetingTypeInput;
  size?: TagSize;
}

function MeetingTypeBadge({ meetingType, size = "md" }: MeetingTypeBadgeProps) {
  const config = MEETING_CONFIG[resolveMeetingType(meetingType)];
  const styles = PILL_BADGE_SIZE_STYLES[size];
  return (
    <HStack
      as="span"
      display="inline-flex"
      spacing={styles.spacing}
      h={styles.h}
      px={styles.px}
      py={styles.py}
      bg={config.bgColor}
      borderRadius={styles.borderRadius}
    >
      <Icon as={config.icon} boxSize={styles.iconSize} color={config.fillColor} />
      <Text
        fontSize={styles.fontSize}
        fontWeight="600"
        lineHeight={styles.lineHeight}
        letterSpacing="0px"
        color={config.fillColor}
        whiteSpace="nowrap"
      >
        {config.label}
      </Text>
    </HStack>
  );
}

// IconBadge variants
function OneOnOneIconBadge({ size }: { size?: TagSize }) {
  return <MeetingTypeIconBadge meetingType="one_on_one" size={size} />;
}

function GroupSessionIconBadge({ size }: { size?: TagSize }) {
  return <MeetingTypeIconBadge meetingType="group_session" size={size} />;
}

function RecurringIconBadge({ size }: { size?: TagSize }) {
  return <MeetingTypeIconBadge meetingType="recurring" size={size} />;
}

// Badge variants
function OneOnOneBadge({ size }: { size?: TagSize }) {
  return <MeetingTypeBadge meetingType="one_on_one" size={size} />;
}

function GroupSessionBadge({ size }: { size?: TagSize }) {
  return <MeetingTypeBadge meetingType="group_session" size={size} />;
}

function RecurringBadge({ size }: { size?: TagSize }) {
  return <MeetingTypeBadge meetingType="recurring" size={size} />;
}

export const AdminMeetingTypeTag = {
  // Dynamic
  IconBadge: MeetingTypeIconBadge,
  Badge: MeetingTypeBadge,
  // 1:1 meeting
  OneOnOneIconBadge,
  OneOnOneBadge,
  // Group Session
  GroupSessionIconBadge,
  GroupSessionBadge,
  // Recurring meeting
  RecurringIconBadge,
  RecurringBadge,
};
